import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCompany } from '../../hooks/useCompany';
import { loanAPI, customerAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  CreditCard,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  DollarSign,
  Calendar,
  Filter,
  Eye,
  Banknote,
  Upload,
  FileText,
  Download,
  Trash2,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(value || 0)}`;
};

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-500/10' },
  approved: { icon: CheckCircle2, color: 'text-blue-600 bg-blue-500/10' },
  disbursed: { icon: Banknote, color: 'text-green-600 bg-green-500/10' },
  paid: { icon: CheckCircle2, color: 'text-green-600 bg-green-500/10' },
  overdue: { icon: AlertTriangle, color: 'text-red-600 bg-red-500/10' },
  defaulted: { icon: XCircle, color: 'text-red-600 bg-red-500/10' },
};

function CreateLoanModal({ isOpen, onClose, onSuccess, companyId }) {
  const [customerId, setCustomerId] = useState('');
  const [loanType, setLoanType] = useState('personal');
  const [principal, setPrincipal] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [termMonths, setTermMonths] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [purpose, setPurpose] = useState('');
  const [collateral, setCollateral] = useState('');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [sourceAccountType, setSourceAccountType] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', companyId],
    queryFn: () => customerAPI.getAll(companyId).then((res) => res.data),
    enabled: !!companyId && isOpen,
  });

  const { data: sourceAccounts = [] } = useQuery({
    queryKey: ['loan-source-accounts', companyId],
    queryFn: () => loanAPI.getSourceAccounts(companyId).then((res) => res.data),
    enabled: !!companyId && isOpen,
  });

  const mutation = useMutation({
    mutationFn: (data) => loanAPI.create(data),
    onSuccess: () => {
      toast.success('Loan application created successfully');
      onSuccess();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    },
  });

  const resetForm = () => {
    setCustomerId('');
    setLoanType('personal');
    setPrincipal('');
    setInterestRate('');
    setTermMonths('');
    setCurrency('USD');
    setPurpose('');
    setCollateral('');
    setSourceAccountId('');
    setSourceAccountType('');
    setDocuments([]);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await loanAPI.uploadDocument(file);
        setDocuments((prev) => [...prev, res.data]);
      }
      toast.success(`${files.length} document(s) uploaded`);
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeDocument = (idx) => {
    setDocuments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSourceChange = (value) => {
    setSourceAccountId(value);
    const found = sourceAccounts.find((a) => a.id === value);
    setSourceAccountType(found?.type || '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customerId || !principal || !interestRate || !termMonths) {
      toast.error('Please fill in all required fields (Customer, Principal, Interest Rate, Term)');
      return;
    }
    mutation.mutate({
      customer_id: customerId,
      company_id: companyId,
      loan_type: loanType,
      principal_amount: parseFloat(principal),
      interest_rate: parseFloat(interestRate),
      term_months: parseInt(termMonths),
      currency,
      purpose: purpose || undefined,
      collateral: collateral || undefined,
      source_account_id: sourceAccountId || undefined,
      source_account_type: sourceAccountType || undefined,
      supporting_documents: documents.length > 0 ? documents : undefined,
    });
  };

  // Calculate preview
  const principalNum = parseFloat(principal) || 0;
  const rateNum = parseFloat(interestRate) || 0;
  const termNum = parseInt(termMonths) || 1;

  let monthlyPayment = 0;
  let totalInterest = 0;
  if (principalNum > 0 && rateNum >= 0 && termNum > 0) {
    totalInterest = principalNum * (rateNum / 100);
    monthlyPayment = (principalNum + totalInterest) / termNum;
  }

  // Filter source accounts by selected currency
  const filteredSources = sourceAccounts.filter((a) => a.currency === currency);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">New Loan Application</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Customer *</label>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="select-customer"
              >
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.customer_number} - {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Loan Type</label>
                <select
                  value={loanType}
                  onChange={(e) => setLoanType(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="select-loan-type"
                >
                  <option value="personal">Personal</option>
                  <option value="business">Business</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="select-currency"
                >
                  <option value="USD">USD ($)</option>
                  <option value="LRD">LRD (L$)</option>
                </select>
              </div>
            </div>

            {/* Source Account */}
            <div>
              <label className="block text-sm font-medium mb-1">Source Account (Where money comes from) *</label>
              <select
                value={sourceAccountId}
                onChange={(e) => handleSourceChange(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="select-source-account"
              >
                <option value="">Select source account</option>
                {filteredSources.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} — Balance: {formatCurrency(a.balance, a.currency)}
                  </option>
                ))}
              </select>
              {filteredSources.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No {currency} accounts available. Create a Chart of Accounts cash account or bank account first.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Principal Amount *</label>
              <input
                type="number"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                placeholder="Enter loan amount"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-principal"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Interest Rate (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  placeholder="Enter rate"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="input-interest-rate"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Term (months) *</label>
                <input
                  type="number"
                  value={termMonths}
                  onChange={(e) => setTermMonths(e.target.value)}
                  placeholder="Enter term"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="input-term"
                />
              </div>
            </div>

            {/* Loan Preview */}
            {principalNum > 0 && rateNum > 0 && termNum > 0 && (
              <div className="p-3 bg-muted/50 rounded-lg space-y-2 text-sm">
                <p className="font-medium">Loan Preview</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-muted-foreground">Monthly Payment</p>
                    <p className="font-semibold">{formatCurrency(monthlyPayment, currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Interest</p>
                    <p className="font-semibold">{formatCurrency(totalInterest, currency)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Amount</p>
                    <p className="font-semibold">{formatCurrency(principalNum + totalInterest, currency)}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Purpose</label>
              <input
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Loan purpose"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-purpose"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Collateral</label>
              <input
                type="text"
                value={collateral}
                onChange={(e) => setCollateral(e.target.value)}
                placeholder="Collateral description (if any)"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-collateral"
              />
            </div>

            {/* Supporting Documents */}
            <div>
              <label className="block text-sm font-medium mb-1">Supporting Documents</label>
              <div className="border border-dashed border-border rounded-md p-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="input-file-upload"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50 w-full justify-center"
                  data-testid="btn-upload-document"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? 'Uploading...' : 'Upload Files (ID, agreements, etc.)'}
                </button>
                {documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between px-2 py-1 bg-muted/50 rounded text-xs">
                        <div className="flex items-center gap-1 truncate">
                          <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{doc.original_name}</span>
                        </div>
                        <button type="button" onClick={() => removeDocument(i)} className="p-0.5 hover:text-red-500">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { onClose(); resetForm(); }} className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                data-testid="button-create-loan"
              >
                {mutation.isPending ? 'Creating...' : 'Create Loan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function LoanDetailModal({ loan, isOpen, onClose, onRefresh }) {
  const queryClient = useQueryClient();
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  const { data: loanDetail } = useQuery({
    queryKey: ['loan-detail', loan?.id],
    queryFn: () => loanAPI.getById(loan.id).then((res) => res.data),
    enabled: !!loan?.id && isOpen,
  });

  const { data: amortization = [] } = useQuery({
    queryKey: ['loan-amortization', loan?.id],
    queryFn: () => loanAPI.getAmortization(loan.id).then((res) => res.data),
    enabled: !!loan?.id && isOpen,
  });

  const approveMutation = useMutation({
    mutationFn: () => loanAPI.approve(loan.id),
    onSuccess: () => {
      toast.success('Loan approved');
      onRefresh();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    },
  });

  const disburseMutation = useMutation({
    mutationFn: () => loanAPI.disburse(loan.id),
    onSuccess: () => {
      toast.success('Loan disbursed — source account debited');
      onRefresh();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to disburse');
    },
  });

  const paymentMutation = useMutation({
    mutationFn: (data) => loanAPI.makePayment(loan.id, data),
    onSuccess: () => {
      toast.success('Payment recorded — source account credited');
      setPaymentAmount('');
      setPaymentRef('');
      onRefresh();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    },
  });

  const handleDownloadReceipt = async (paymentId) => {
    setDownloadingReceipt(paymentId);
    try {
      const res = await loanAPI.downloadReceipt(loan.id, paymentId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt_${loan.loan_number}_${paymentId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch {
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(null);
    }
  };

  if (!isOpen || !loan) return null;

  const detail = loanDetail || loan;
  const payments = detail.payments || [];
  const docs = detail.supporting_documents || loan.supporting_documents || [];
  const StatusIcon = STATUS_CONFIG[loan.status]?.icon || Clock;
  const statusColor = STATUS_CONFIG[loan.status]?.color || 'text-gray-600 bg-gray-500/10';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">{loan.loan_number}</h3>
              <p className="text-sm text-muted-foreground">{loan.customer_name}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${statusColor}`}>
              <StatusIcon className="h-4 w-4" />
              {loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
            </span>
          </div>

          {/* Loan Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Principal</p>
              <p className="font-semibold">{formatCurrency(loan.principal_amount, loan.currency)}</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Interest Rate</p>
              <p className="font-semibold">{loan.interest_rate}%</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Term</p>
              <p className="font-semibold">{loan.term_months} months</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Monthly Payment</p>
              <p className="font-semibold">{formatCurrency(loan.monthly_payment, loan.currency)}</p>
            </div>
          </div>

          {/* Source Account */}
          {(loan.source_account_name || detail.source_account_name) && (
            <div className="mb-4 p-3 bg-blue-500/10 rounded-lg flex items-center gap-2">
              <Banknote className="h-4 w-4 text-blue-600" />
              <span className="text-sm">
                <span className="font-medium">Source:</span> {loan.source_account_name || detail.source_account_name}
              </span>
            </div>
          )}

          {/* Balance Info */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <p className="text-xs text-muted-foreground">Amount Paid</p>
              <p className="font-semibold text-green-600">{formatCurrency(loan.amount_paid, loan.currency)}</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <p className="text-xs text-muted-foreground">Remaining Balance</p>
              <p className="font-semibold text-blue-600">{formatCurrency(loan.remaining_balance, loan.currency)}</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Total Amount</p>
              <p className="font-semibold">{formatCurrency(loan.total_amount, loan.currency)}</p>
            </div>
          </div>

          {/* Supporting Documents */}
          {docs.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">Supporting Documents</h4>
              <div className="space-y-1">
                {docs.map((doc, i) => (
                  <a
                    key={i}
                    href={loanAPI.getDocumentUrl(doc.filename)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded text-sm hover:bg-muted transition"
                    data-testid={`loan-document-${i}`}
                  >
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate flex-1">{doc.original_name}</span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            {loan.status === 'pending' && (
              <button
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                data-testid="button-approve"
              >
                <CheckCircle2 className="h-4 w-4" />
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </button>
            )}
            {loan.status === 'approved' && (
              <button
                onClick={() => disburseMutation.mutate()}
                disabled={disburseMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                data-testid="button-disburse"
              >
                <Banknote className="h-4 w-4" />
                {disburseMutation.isPending ? 'Disbursing...' : 'Disburse'}
              </button>
            )}
            {['disbursed', 'overdue'].includes(loan.status) && (
              <div className="flex flex-wrap items-center gap-2 w-full">
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Amount"
                  className="w-28 px-3 py-2 bg-background border border-border rounded-md text-sm"
                  data-testid="input-payment"
                />
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="px-3 py-2 bg-background border border-border rounded-md text-sm"
                  data-testid="select-payment-method"
                >
                  <option value="cash">Cash</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  placeholder="Reference (optional)"
                  className="flex-1 min-w-[120px] px-3 py-2 bg-background border border-border rounded-md text-sm"
                  data-testid="input-payment-ref"
                />
                <button
                  onClick={() => paymentMutation.mutate({
                    loan_id: loan.id,
                    amount: parseFloat(paymentAmount),
                    payment_type: 'regular',
                    payment_method: paymentMethod,
                    reference: paymentRef || undefined,
                  })}
                  disabled={!paymentAmount || paymentMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                  data-testid="button-make-payment"
                >
                  <DollarSign className="h-4 w-4" />
                  {paymentMutation.isPending ? 'Processing...' : 'Record Payment'}
                </button>
              </div>
            )}
          </div>

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold mb-3">Payment History</h4>
              <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                      <th className="text-right py-2 px-3 font-medium">Amount</th>
                      <th className="text-left py-2 px-3 font-medium">Method</th>
                      <th className="text-right py-2 px-3 font-medium">Balance After</th>
                      <th className="text-center py-2 px-3 font-medium">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t border-border/50">
                        <td className="py-2 px-3">{new Date(p.received_at).toLocaleDateString()}</td>
                        <td className="py-2 px-3 text-right font-mono font-medium text-green-600">
                          {formatCurrency(p.amount, loan.currency)}
                        </td>
                        <td className="py-2 px-3 capitalize text-xs">{(p.payment_method || 'cash').replace('_', ' ')}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(p.balance_after, loan.currency)}</td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => handleDownloadReceipt(p.id)}
                            disabled={downloadingReceipt === p.id}
                            className="p-1 hover:bg-muted rounded transition disabled:opacity-50"
                            title="Download Receipt"
                            data-testid={`receipt-${p.id}`}
                          >
                            {downloadingReceipt === p.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : (
                              <Download className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Amortization Schedule */}
          {amortization.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Amortization Schedule</h4>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium">#</th>
                      <th className="text-left py-2 px-3 font-medium">Due Date</th>
                      <th className="text-right py-2 px-3 font-medium">Principal</th>
                      <th className="text-right py-2 px-3 font-medium">Interest</th>
                      <th className="text-right py-2 px-3 font-medium">Total</th>
                      <th className="text-right py-2 px-3 font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amortization.map((row) => (
                      <tr key={row.payment_number} className="border-t border-border/50">
                        <td className="py-2 px-3">{row.payment_number}</td>
                        <td className="py-2 px-3">{row.due_date}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(row.principal, loan.currency)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(row.interest, loan.currency)}</td>
                        <td className="py-2 px-3 text-right font-mono font-medium">{formatCurrency(row.total_payment, loan.currency)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(row.remaining_balance, loan.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-border rounded-md hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Loans() {
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;
  const customerId = searchParams.get('customer_id');

  const { data: loans = [], isLoading } = useQuery({
    queryKey: ['loans', companyId, statusFilter, customerId],
    queryFn: () => loanAPI.getAll(companyId, statusFilter, customerId).then((res) => res.data),
    enabled: !!companyId,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['loans']);
    queryClient.invalidateQueries(['loan-detail']);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Loans can be USD or LRD (loan.currency) -- sum each separately rather
  // than blending both into one number the way this used to.
  const disbursedLoans = loans.filter((l) => l.status === 'disbursed');
  const totalDisbursedUsd = disbursedLoans.filter((l) => l.currency !== 'LRD').reduce((sum, l) => sum + (l.remaining_balance || 0), 0);
  const totalDisbursedLrd = disbursedLoans.filter((l) => l.currency === 'LRD').reduce((sum, l) => sum + (l.remaining_balance || 0), 0);
  const totalOverdue = loans.filter((l) => l.status === 'overdue').length;

  return (
    <div className="space-y-6" data-testid="loans-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Loan Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage loan applications and track repayments
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          data-testid="button-new-loan"
        >
          <Plus className="h-4 w-4" />
          New Loan
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Loans</span>
          </div>
          <p className="text-2xl font-semibold">{loans.length}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-muted-foreground">Pending</span>
          </div>
          <p className="text-2xl font-semibold">{loans.filter((l) => l.status === 'pending').length}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="text-sm text-muted-foreground">Outstanding</span>
          </div>
          <p className="text-2xl font-semibold text-green-600">{formatCurrency(totalDisbursedUsd, 'USD')}</p>
          {totalDisbursedLrd > 0 && (
            <p className="text-sm font-medium text-blue-600">{formatCurrency(totalDisbursedLrd, 'LRD')}</p>
          )}
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-muted-foreground">Overdue</span>
          </div>
          <p className="text-2xl font-semibold text-red-600">{totalOverdue}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 bg-background border border-border rounded-md text-sm"
          data-testid="select-status-filter"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="disbursed">Disbursed</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {/* Loans Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 font-medium">Loan #</th>
                <th className="text-left py-3 px-4 font-medium">Customer</th>
                <th className="text-left py-3 px-4 font-medium">Type</th>
                <th className="text-left py-3 px-4 font-medium">Source</th>
                <th className="text-right py-3 px-4 font-medium">Principal</th>
                <th className="text-right py-3 px-4 font-medium">Rate</th>
                <th className="text-right py-3 px-4 font-medium">Balance</th>
                <th className="text-center py-3 px-4 font-medium">Status</th>
                <th className="text-center py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => {
                const LoanStatusIcon = STATUS_CONFIG[loan.status]?.icon || Clock;
                const loanStatusColor = STATUS_CONFIG[loan.status]?.color || 'text-gray-600 bg-gray-500/10';
                return (
                  <tr key={loan.id} className="border-t border-border/50 hover:bg-muted/30">
                    <td className="py-3 px-4 font-medium">{loan.loan_number}</td>
                    <td className="py-3 px-4">{loan.customer_name}</td>
                    <td className="py-3 px-4 capitalize">{loan.loan_type}</td>
                    <td className="py-3 px-4 text-xs text-muted-foreground truncate max-w-[120px]">
                      {loan.source_account_name || '—'}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {formatCurrency(loan.principal_amount, loan.currency)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-xs">
                      {loan.interest_rate}%
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {formatCurrency(loan.remaining_balance, loan.currency)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${loanStatusColor}`}>
                        <LoanStatusIcon className="h-3 w-3" />
                        {loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setSelectedLoan(loan)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded"
                        data-testid={`button-view-${loan.id}`}
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
              {loans.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-muted-foreground">
                    No loans found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateLoanModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
      />
      <LoanDetailModal
        loan={selectedLoan}
        isOpen={!!selectedLoan}
        onClose={() => setSelectedLoan(null)}
        onRefresh={handleRefresh}
      />
    </div>
  );
}
